import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  IconButton,
  Fab,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person,
  Category,
  Title,
  Business,
  Event,
  CheckCircle,
  Cancel,
  Refresh,
  Download,
  Login,
  Logout,
  Info,
  CameraAlt,
  CameraEnhance,
  ArrowBack,
  Close,
} from '@mui/icons-material';
import Select from 'react-select';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../utils/api';

const PassScanner = ({ accessPointId, onBack, eventName, accessPointName, isEntryExit = false }) => {
  const [qrCode, setQrCode] = useState('');
  const [passData, setPassData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [entryCount, setEntryCount] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [operationMode, setOperationMode] = useState('entry'); // 'entry' or 'exit'
  const [currentStatus, setCurrentStatus] = useState(null);
  const [passesList, setPassesList] = useState([]);
  const [selectedPass, setSelectedPass] = useState(null);
  const [loadingPasses, setLoadingPasses] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [lastScanTime, setLastScanTime] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('');
  const [postSuccessCooldown, setPostSuccessCooldown] = useState(false);
  const inputRef = useRef(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const isProcessingRef = useRef(false);
  const postSuccessCooldownRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (inputRef.current && !isScanning) {
      inputRef.current.focus();
    }
    fetchEntryCount();
    fetchPassesList();
    
    // Cleanup scanner on unmount
    return () => {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch(err => {
          console.error('Error stopping scanner:', err);
        });
      }
    };
  }, [eventName, isScanning]);

  // Prevent body scroll when camera is open on mobile
  useEffect(() => {
    if (isScanning && isMobile) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isScanning, isMobile]);

  const fetchEntryCount = async () => {
    try {
      const response = await api.get(`/api/access-point-entry-count/?event=${eventName}`);
      const counts = response.data;
      const currentAccessPointCount = counts.find(
        (item) => item.access_point__id === accessPointId
      );
      setEntryCount(currentAccessPointCount ? currentAccessPointCount.count : 0);
    } catch (error) {
      console.error('Error fetching entry count:', error);
    }
  };

  const fetchPassesList = async () => {
    if (!eventName) return;
    
    setLoadingPasses(true);
    try {
      const response = await api.get(`/api/entry-pass-list/?event=${eventName}&limit=1000`);
      const passes = response.data.results.map(pass => ({
        value: pass.reg_no,
        label: `${pass.name} (${pass.reg_no})`,
        name: pass.name,
        reg_no: pass.reg_no,
        email: pass.email,
        phone: pass.phone
      }));
      setPassesList(passes);
    } catch (error) {
      console.error('Error fetching passes list:', error);
      setError('Failed to load passes list');
    } finally {
      setLoadingPasses(false);
    }
  };

  const checkStatus = async (qrCode) => {
    try {
      const response = await api.get('/api/access-point-status/', {
        params: {
          event: eventName,
          access_point: accessPointId,
          qr_code: qrCode
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking status:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPassData(null);
    setCurrentStatus(null);

    try {
      let response;
      
      if (operationMode === 'entry') {
        // Handle entry
        response = await api.post('/api/access-point-entry/', {
          qr_code: qrCode,
          access_point: accessPointId,
          event: eventName
        });
      } else {
        // Handle exit
        response = await api.post('/api/access-point-exit/', {
          qr_code: qrCode,
          access_point: accessPointId,
          event: eventName
        });
      }
      
      setPassData({
        name: response.data.name,
        pass_type: response.data.designation,
        category: response.data.designation,
        is_allowed: response.data.message === 'success' || response.data.message === 'Exit successful' || response.data.message === 'Entry successful',
        created: response.data.created,
        created_at: new Date().toISOString(),
        status: response.data.status,
        photo: response.data.photo,
        admit: response.data.admit_max
      });
      
      if (response.data.message === 'success' || response.data.message === 'Exit successful' || response.data.message === 'Entry successful') {
        fetchEntryCount();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred while processing the request.');
      if (error.response?.data?.name && error.response?.data?.designation) {
        setPassData({
          name: error.response.data.name,
          pass_type: error.response.data.designation,
          is_allowed: false,
          created: error.response.data.created,
          created_at: new Date().toISOString(),
          status: error.response.data.status,
          photo: error.response.data.photo,
          admit: error.response.data.admit_max
        });
      }
    } finally {
      setLoading(false);
      setQrCode('');
      setSelectedPass(null);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handlePassSelect = (selectedOption) => {
    setSelectedPass(selectedOption);
    if (selectedOption) {
      setQrCode(selectedOption.reg_no);
    } else {
      setQrCode('');
    }
  };

  const handleStatusCheck = async () => {
    if (!qrCode.trim()) {
      setError('Please enter a QR code to check status');
      return;
    }

    setLoading(true);
    setError('');
    setPassData(null);

    try {
      const statusData = await checkStatus(qrCode);
      if (statusData) {
        setCurrentStatus(statusData);
        setPassData({
          name: statusData.name,
          pass_type: statusData.designation,
          category: statusData.designation,
          is_allowed: true,
          created: true,
          created_at: new Date().toISOString(),
          status: statusData.status,
          photo: statusData.photo,
          admit: statusData.admit_max,
          email: statusData.email,
          phone: statusData.phone,
          institution: statusData.institution,
          original_designation: statusData.original_designation,
          event_participation: statusData.event_participation,
          poster_title: statusData.poster_title
        });
      }
    } catch (error) {
      setError('Failed to check status. Please try again.');
    } finally {
      setLoading(false);
      setQrCode('');
      setSelectedPass(null);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const startCameraScan = async () => {
    try {
      setCameraError('');
      setIsScanning(true);
      
      // Wait for React to render the DOM element (poll until it exists)
      let element = document.getElementById('qr-reader');
      let attempts = 0;
      const maxAttempts = 20; // Wait up to 2 seconds (20 * 100ms)
      
      while (!element && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        element = document.getElementById('qr-reader');
        attempts++;
      }
      
      if (!element) {
        throw new Error('Scanner element not found. Please try again.');
      }
      
      // Create Html5Qrcode instance
      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      // Get available cameras
      const cameras = await Html5Qrcode.getCameras();
      if (cameras && cameras.length > 0) {
        setAvailableCameras(cameras);
        
        // Select camera: prefer back camera on mobile, or use selected camera
        let cameraId;
        if (selectedCameraId) {
          cameraId = selectedCameraId;
        } else {
          // Try to find back camera (usually labeled as "environment" or "back")
          const backCamera = cameras.find(cam => {
            const label = cam.label || '';
            return label.toLowerCase().includes('back') || 
                   label.toLowerCase().includes('environment') ||
                   label.toLowerCase().includes('rear');
          });
          cameraId = backCamera ? backCamera.id : cameras[0].id;
        }
        
        // Calculate QR box size based on screen width (responsive)
        const qrboxSize = Math.min(300, window.innerWidth * 0.7);
        
        await html5QrCode.start(
          cameraId,
          {
            fps: 10,
            qrbox: { width: qrboxSize, height: qrboxSize },
            aspectRatio: 1.0,
          },
          (decodedText, decodedResult) => {
            // QR code scanned successfully
            handleScannedQRCode(decodedText);
          },
          (errorMessage) => {
            // Ignore scanning errors (they're frequent during scanning)
          }
        );
      } else {
        throw new Error('No cameras found. Please ensure your device has a camera and grant camera permissions.');
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      let errorMessage = 'Failed to access camera. ';
      const errorMsg = String(error?.message || error?.toString() || '');
      if (errorMsg && (errorMsg.includes('Permission denied') || errorMsg.includes('NotAllowedError'))) {
        errorMessage += 'Please grant camera permissions in your browser settings.';
      } else if (errorMsg && (errorMsg.includes('NotFoundError') || errorMsg.includes('No cameras'))) {
        errorMessage += 'No camera found. Please ensure your device has a camera connected.';
      } else if (errorMsg && errorMsg.includes('not found')) {
        errorMessage += 'Scanner element not ready. Please try again.';
      } else {
        errorMessage += errorMsg || 'Please check your camera permissions and try again.';
      }
      setCameraError(errorMessage);
      setIsScanning(false);
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current = null;
      }
    }
  };

  const stopCameraScan = async () => {
    try {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      }
      html5QrCodeRef.current = null;
      setIsScanning(false);
      setCameraError('');
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.error('Error stopping camera:', error);
      setIsScanning(false);
      html5QrCodeRef.current = null;
    }
  };

  const handleScannedQRCode = async (scannedCode) => {
    // Prevent multiple simultaneous processing
    if (isProcessingRef.current) {
      return;
    }
    
    // Prevent scanning during post-success cooldown (check ref for immediate check)
    if (postSuccessCooldownRef.current || postSuccessCooldown) {
      return;
    }
    
    // Prevent duplicate scans - check if same code was scanned recently (within 3 seconds)
    const currentTime = Date.now();
    const timeSinceLastScan = currentTime - lastScanTime;
    const SCAN_COOLDOWN = 3000; // 3 seconds cooldown
    
    if (scannedCode === lastScannedCode && timeSinceLastScan < SCAN_COOLDOWN) {
      // Same code scanned too soon, ignore
      return;
    }
    
    // Set processing flag to prevent concurrent calls
    isProcessingRef.current = true;
    
    // Update last scanned code and time
    setLastScannedCode(scannedCode);
    setLastScanTime(currentTime);
    
    // Don't stop scanning - keep camera open for continuous scanning
    // Set the scanned QR code
    setQrCode(scannedCode);
    setSelectedPass(null);
    setProcessingMessage('Processing QR code...');
    
    // Automatically process the scanned QR code
    if (operationMode === 'status') {
      // For status check, trigger the status check
      setLoading(true);
      setError('');
      setPassData(null);

      try {
        const statusData = await checkStatus(scannedCode);
        if (statusData) {
          setCurrentStatus(statusData);
          setPassData({
            name: statusData.name,
            pass_type: statusData.designation,
            category: statusData.designation,
            is_allowed: true,
            created: true,
            created_at: new Date().toISOString(),
            status: statusData.status,
            photo: statusData.photo,
            admit: statusData.admit_max
          });
        }
      } catch (error) {
        setError('Failed to check status. Please try again.');
      } finally {
        setLoading(false);
        setQrCode('');
        setSelectedPass(null);
        setProcessingMessage('');
        isProcessingRef.current = false;
        // Add 3-second delay after every QR detection (success or failure)
        postSuccessCooldownRef.current = true;
        setPostSuccessCooldown(true);
        setTimeout(() => {
          postSuccessCooldownRef.current = false;
          setPostSuccessCooldown(false);
        }, 2000);
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    } else {
      // For entry/exit, submit the form
      setLoading(true);
      setError('');
      setPassData(null);

      try {
        let response;
        
        if (operationMode === 'entry') {
          response = await api.post('/api/access-point-entry/', {
            qr_code: scannedCode,
            access_point: accessPointId,
            event: eventName
          });
        } else {
          response = await api.post('/api/access-point-exit/', {
            qr_code: scannedCode,
            access_point: accessPointId,
            event: eventName
          });
        }
        
        setPassData({
          name: response.data.name,
          pass_type: response.data.designation,
          category: response.data.designation,
          is_allowed: response.data.message === 'success' || response.data.message === 'Exit successful' || response.data.message === 'Entry successful',
          created: response.data.created,
          created_at: new Date().toISOString(),
          status: response.data.status,
          photo: response.data.photo,
          admit: response.data.admit_max,
          email: response.data.email,
          phone: response.data.phone,
          institution: response.data.institution,
          original_designation: response.data.original_designation,
          event_participation: response.data.event_participation,
          poster_title: response.data.poster_title
        });
        
        if (response.data.message === 'success' || response.data.message === 'Exit successful' || response.data.message === 'Entry successful') {
          fetchEntryCount();
        }
      } catch (error) {
        setError(error.response?.data?.message || 'An error occurred while processing the request.');
        if (error.response?.data?.name && error.response?.data?.designation) {
          setPassData({
            name: error.response.data.name,
            pass_type: error.response.data.designation,
            is_allowed: false,
            created: error.response.data.created,
            created_at: new Date().toISOString(),
            status: error.response.data.status,
            photo: error.response.data.photo,
            admit: error.response.data.admit_max,
            email: error.response.data.email,
            phone: error.response.data.phone,
            institution: error.response.data.institution,
            original_designation: error.response.data.original_designation,
            event_participation: error.response.data.event_participation,
            poster_title: error.response.data.poster_title
          });
        }
      } finally {
        setLoading(false);
        setQrCode('');
        setSelectedPass(null);
        setProcessingMessage('');
        isProcessingRef.current = false;
        // Add 3-second delay after every QR detection (success or failure)
        postSuccessCooldownRef.current = true;
        setPostSuccessCooldown(true);
        setTimeout(() => {
          postSuccessCooldownRef.current = false;
          setPostSuccessCooldown(false);
        }, 3000);
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    }
  };

  const handleDownloadEntries = async () => {
    try {
      setDownloading(true);
      // Use the API endpoint to download the CSV file
      const response = await api.get(`/api/access-point-download/${accessPointId}/`, {
        responseType: 'blob', // Important for handling file downloads
      });
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a temporary link element to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${accessPointName.replace(/\s+/g, '_')}_entries.csv`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading entries:', error);
      setError('Failed to download entries. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'entered':
        return 'success';
      case 'exited':
        return 'warning';
      case 'not_entered':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'entered':
        return <Login />;
      case 'exited':
        return <Logout />;
      case 'not_entered':
        return <Info />;
      default:
        return <Info />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f7fa',
        pb: isMobile ? 2 : 4,
        position: 'relative',
      }}
    >
      {/* Mobile Header */}
      {isMobile && (
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            bgcolor: '#fff',
            borderBottom: '1px solid #e0e0e0',
            px: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={onBack}
              sx={{
                bgcolor: '#f5f5f5',
                '&:hover': { bgcolor: '#e0e0e0' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {accessPointName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                {entryCount} entries
              </Typography>
            </Box>
            <Button
              variant="text"
              size="small"
              startIcon={<Download />}
              onClick={handleDownloadEntries}
              disabled={downloading || entryCount === 0}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              {downloading ? <CircularProgress size={16} /> : null}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Desktop Header */}
      {!isMobile && (
        <Box sx={{ mb: 3, px: 2 }}>
          <Button
            onClick={onBack}
            variant="outlined"
            startIcon={<ArrowBack />}
            sx={{ mb: 2 }}
          >
            Back to Access Points
          </Button>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Scan Pass - {accessPointName}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Total Entries: {entryCount}
          </Typography>
          
          {isEntryExit && (
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              This access point supports both entry and exit functionality.
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Download />}
              onClick={handleDownloadEntries}
              disabled={downloading || entryCount === 0}
            >
              {downloading ? <CircularProgress size={24} /> : 'Download Entries'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Operation Mode Toggle */}
      {isEntryExit && (
        <Box sx={{ mb: isMobile ? 2 : 3, px: isMobile ? 2 : 0 }}>
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 1 : 0,
              bgcolor: '#fff',
              borderRadius: isMobile ? 2 : 1,
              border: isMobile ? 'none' : '1px solid #e0e0e0',
            }}
          >
            <ToggleButtonGroup
              value={operationMode}
              exclusive
              onChange={(e, newMode) => newMode && setOperationMode(newMode)}
              aria-label="operation mode"
              fullWidth={isMobile}
              sx={{
                '& .MuiToggleButton-root': {
                  py: isMobile ? 1.5 : 1,
                  px: isMobile ? 2 : 1.5,
                  fontSize: isMobile ? '0.95rem' : '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  border: 'none',
                  '&.Mui-selected': {
                    bgcolor: theme.palette.primary.main,
                    color: '#fff',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="entry" aria-label="entry">
                <Login sx={{ mr: 1, fontSize: isMobile ? '1.2rem' : '1rem' }} />
                Entry
              </ToggleButton>
              <ToggleButton value="exit" aria-label="exit">
                <Logout sx={{ mr: 1, fontSize: isMobile ? '1.2rem' : '1rem' }} />
                Exit
              </ToggleButton>
              <ToggleButton value="status" aria-label="status">
                <Info sx={{ mr: 1, fontSize: isMobile ? '1.2rem' : '1rem' }} />
                Status
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Box>
      )}
      
      {/* Main Content Container - Split view on mobile when scanning */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile && isScanning ? 'column' : 'column',
          height: isMobile && isScanning ? 'calc(100vh - 120px)' : 'auto',
          overflow: 'hidden',
        }}
      >
        {/* Camera Scanner Section */}
        <Box sx={{ px: isMobile ? 2 : 0, mb: isMobile && isScanning ? 0 : 2 }}>
          {!isScanning && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth={isMobile}
                startIcon={<CameraAlt />}
                onClick={startCameraScan}
                disabled={loading}
                sx={{
                  py: isMobile ? 1.5 : 1.25,
                  fontSize: isMobile ? '1rem' : '0.875rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: isMobile ? 2 : 1,
                  boxShadow: isMobile ? 2 : 1,
                }}
              >
                Scan QR Code with Camera
              </Button>
              
              {!isScanning && availableCameras.length > 1 && !isMobile && (
                <Box sx={{ minWidth: 200, mt: 2 }}>
                  <Select
                    options={availableCameras.map(cam => ({
                      value: cam.id,
                      label: cam.label || `Camera ${cam.id.substring(0, 8)}`
                    }))}
                    value={availableCameras.find(cam => cam.id === selectedCameraId) 
                      ? { value: selectedCameraId, label: availableCameras.find(cam => cam.id === selectedCameraId).label || `Camera ${selectedCameraId.substring(0, 8)}` }
                      : null}
                    onChange={(option) => setSelectedCameraId(option?.value || null)}
                    placeholder="Select camera..."
                    isClearable
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: 40,
                        fontSize: '0.875rem',
                      }),
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
          
          {cameraError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: isMobile ? 2 : 1 }}>
              {cameraError}
            </Alert>
          )}
          
          {isScanning && (
            <Box
              sx={{
                position: 'relative',
                bgcolor: '#000',
                width: '100%',
                height: isMobile ? '35vh' : '400px',
                maxHeight: isMobile ? '35vh' : '400px',
                minHeight: isMobile ? '35vh' : '400px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: isMobile ? 0 : 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 2001,
                }}
              >
                <IconButton
                  onClick={stopCameraScan}
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
              
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 2001,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 500,
                  }}
                >
                  Camera Scanner
                </Typography>
              </Box>
              
              {(processingMessage || postSuccessCooldown) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2001,
                    textAlign: 'center',
                  }}
                >
                  <Paper
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.8)',
                      color: '#fff',
                      px: 3,
                      py: 2,
                      borderRadius: 2,
                    }}
                  >
                    {processingMessage && (
                      <>
                        <CircularProgress size={24} sx={{ color: '#fff', mb: 1 }} />
                        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                          {processingMessage}
                        </Typography>
                      </>
                    )}
                    {postSuccessCooldown && !processingMessage && (
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                        Please wait a moment before next scan...
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}
              
              <Box
                id="qr-reader"
                ref={scannerRef}
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& video': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  },
                  '& #qr-reader__dashboard_section': {
                    display: 'none',
                  },
                }}
              />
              
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2001,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 500,
                  }}
                >
                  Point camera at QR code
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Details Section - Scrollable on mobile when scanning */}
        <Box
          sx={{
            px: isMobile ? 2 : 0,
            height: isMobile && isScanning ? 'calc(65vh - 20px)' : 'auto',
            overflowY: isMobile && isScanning ? 'auto' : 'visible',
            overflowX: 'hidden',
            flex: isMobile && isScanning ? '1 1 auto' : '0 0 auto',
          }}
        >
          {/* Input Form Section */}
          {!isScanning && (
            <Paper
              elevation={0}
              sx={{
                p: isMobile ? 2 : 3,
                bgcolor: '#fff',
                borderRadius: isMobile ? 2 : 1,
                mb: 2,
              }}
            >
            <form onSubmit={handleSubmit}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                  fontSize: isMobile ? '0.875rem' : '0.875rem',
                  color: 'text.primary',
                }}
              >
                Select Pass or Enter QR Code
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Select
                  options={passesList}
                  value={selectedPass}
                  onChange={handlePassSelect}
                  placeholder="Search by name..."
                  isSearchable
                  isLoading={loadingPasses}
                  noOptionsMessage={() => "No passes found"}
                  isClearable
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: isMobile ? 52 : 48,
                      fontSize: isMobile ? '1rem' : '0.875rem',
                      borderColor: state.isFocused ? theme.palette.primary.main : '#c4c4c4',
                      borderWidth: 1.5,
                      borderRadius: isMobile ? 2 : 1,
                      boxShadow: state.isFocused
                        ? `0 0 0 2px ${theme.palette.primary.main}20`
                        : 'none',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: isMobile ? '1rem' : '0.875rem',
                      padding: isMobile ? '12px 16px' : '10px 14px',
                      backgroundColor: state.isSelected
                        ? theme.palette.primary.main
                        : state.isFocused
                          ? '#e3f2fd'
                          : 'white',
                      color: state.isSelected ? 'white' : 'black',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#999',
                    }),
                  }}
                />
              </Box>
              
              {selectedPass && (
                <Box
                  sx={{
                    mb: 2,
                    p: isMobile ? 1.5 : 2,
                    bgcolor: '#f0f7ff',
                    borderRadius: isMobile ? 2 : 1,
                    border: `1px solid ${theme.palette.primary.light}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: isMobile ? '0.875rem' : '0.875rem',
                      color: 'text.primary',
                    }}
                  >
                    <strong>{selectedPass.name}</strong>
                    <br />
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      {selectedPass.reg_no}
                    </Typography>
                  </Typography>
                </Box>
              )}
              
              <TextField
                fullWidth
                label="QR Code"
                value={qrCode}
                onChange={(e) => {
                  setQrCode(e.target.value);
                  if (selectedPass && e.target.value !== selectedPass.reg_no) {
                    setSelectedPass(null);
                  }
                }}
                inputRef={inputRef}
                autoFocus={!isMobile}
                disabled={isScanning}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: isMobile ? 2 : 1,
                    fontSize: isMobile ? '1rem' : '0.875rem',
                    '& fieldset': {
                      borderWidth: 1.5,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: isMobile ? '1rem' : '0.875rem',
                  },
                }}
                helperText={
                  isMobile
                    ? 'Enter QR code or scan with camera'
                    : 'You can either select from the dropdown above, manually enter a QR code, or use the camera scanner above'
                }
              />
              
              <Box sx={{ mt: 3 }}>
                {operationMode === 'status' ? (
                  <Button
                    variant="contained"
                    onClick={handleStatusCheck}
                    disabled={loading || isScanning || !qrCode.trim()}
                    fullWidth={isMobile}
                    size={isMobile ? 'large' : 'medium'}
                    sx={{
                      py: isMobile ? 1.5 : 1.25,
                      fontSize: isMobile ? '1rem' : '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: isMobile ? 2 : 1,
                      boxShadow: 2,
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Check Status'
                    )}
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || isScanning || !qrCode.trim()}
                    fullWidth={isMobile}
                    size={isMobile ? 'large' : 'medium'}
                    sx={{
                      py: isMobile ? 1.5 : 1.25,
                      fontSize: isMobile ? '1rem' : '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: isMobile ? 2 : 1,
                      boxShadow: 2,
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      operationMode === 'entry' ? 'Process Entry' : 'Process Exit'
                    )}
                  </Button>
                )}
              </Box>
            </form>
          </Paper>
          )}
      
          {/* Error Message */}
          {error && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity="error"
                sx={{
                  borderRadius: isMobile ? 2 : 1,
                  fontSize: isMobile ? '0.875rem' : '0.875rem',
                }}
              >
                {error}
              </Alert>
            </Box>
          )}
      
          {/* Pass Data Display */}
          {passData && (
            <Box sx={{ mb: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: isMobile ? 2 : 1,
                  overflow: 'hidden',
                  border: `2px solid ${
                    passData.is_allowed
                      ? theme.palette.success.main
                      : passData.hasOwnProperty('is_allowed')
                        ? theme.palette.error.main
                        : theme.palette.primary.main
                  }`,
                }}
              >
            {passData.photo && (
              <Box
                sx={{
                  width: '100%',
                  height: isMobile ? 250 : 300,
                  position: 'relative',
                  bgcolor: '#f5f5f5',
                }}
              >
                <CardMedia
                  component="img"
                  image={
                    passData.photo.startsWith('data:')
                      ? passData.photo
                      : `data:image/jpeg;base64,${passData.photo}`
                  }
                  alt="Pass Photo"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {passData.hasOwnProperty('is_allowed') && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                    }}
                  >
                    <Chip
                      icon={passData.is_allowed ? <CheckCircle /> : <Cancel />}
                      label={passData.is_allowed ? 'Allowed' : 'Denied'}
                      color={passData.is_allowed ? 'success' : 'error'}
                      sx={{
                        fontWeight: 600,
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        height: isMobile ? 32 : 36,
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
            
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip
                  label={passData.pass_type}
                  color="primary"
                  sx={{
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    height: isMobile ? 28 : 32,
                    fontWeight: 500,
                  }}
                />
                {passData.created === false && (
                  <Chip
                    icon={<Refresh sx={{ fontSize: isMobile ? '1rem' : '1.2rem' }} />}
                    label="Already Scanned"
                    color="warning"
                    sx={{
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      height: isMobile ? 28 : 32,
                    }}
                  />
                )}
                {passData.status && (
                  <Chip
                    icon={getStatusIcon(passData.status)}
                    label={passData.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(passData.status)}
                    sx={{
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      height: isMobile ? 28 : 32,
                    }}
                  />
                )}
              </Box>
              
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  fontSize: isMobile ? '1.25rem' : '1.5rem',
                }}
              >
                {passData.name}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {passData.email && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: 'block', mb: 0.5 }}
                    >
                      Email
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isMobile ? '0.875rem' : '0.875rem', fontWeight: 500 }}
                    >
                      {passData.email}
                    </Typography>
                  </Box>
                )}
                
                {passData.phone && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: 'block', mb: 0.5 }}
                    >
                      Phone
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isMobile ? '0.875rem' : '0.875rem', fontWeight: 500 }}
                    >
                      {passData.phone}
                    </Typography>
                  </Box>
                )}
                
                {passData.institution && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: 'block', mb: 0.5 }}
                    >
                      <Business sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mr: 0.5 }} />
                      Institution
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isMobile ? '0.875rem' : '0.875rem', fontWeight: 500 }}
                    >
                      {passData.institution}
                    </Typography>
                  </Box>
                )}
                
                {passData.original_designation && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: 'block', mb: 0.5 }}
                    >
                      <Category sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mr: 0.5 }} />
                      Original Designation
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isMobile ? '0.875rem' : '0.875rem', fontWeight: 500 }}
                    >
                      {passData.original_designation}
                    </Typography>
                  </Box>
                )}
                
                {passData.event_participation && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: 'block', mb: 0.5 }}
                    >
                      <Event sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mr: 0.5 }} />
                      Event Participation
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isMobile ? '0.875rem' : '0.875rem', fontWeight: 500 }}
                    >
                      {passData.event_participation}
                    </Typography>
                  </Box>
                )}
                
                {passData.poster_title && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: 'block', mb: 0.5 }}
                    >
                      <Title sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mr: 0.5 }} />
                      Poster Title
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isMobile ? '0.875rem' : '0.875rem', fontWeight: 500 }}
                    >
                      {passData.poster_title}
                    </Typography>
                  </Box>
                )}
                
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: 'block', mb: 0.5 }}
                  >
                    <Event sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mr: 0.5 }} />
                    Accompanying Passes
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: isMobile ? '0.875rem' : '0.875rem', fontWeight: 500 }}
                  >
                    {passData.admit || 0}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: 'block', mb: 0.5 }}
                  >
                    Scanned At
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: isMobile ? '0.875rem' : '0.875rem', fontWeight: 500 }}
                  >
                    {new Date(passData.created_at).toLocaleString()}
                  </Typography>
                </Box>
                
                {passData.category && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: 'block', mb: 0.5 }}
                    >
                      <Category sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mr: 0.5 }} />
                      Category
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isMobile ? '0.875rem' : '0.875rem', fontWeight: 500 }}
                    >
                      {passData.category}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Paper>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PassScanner;